import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {immer} from 'zustand/middleware/immer';
import {List} from 'immutable';

import type {ClassroomInvitationModelV1, ClassroomModelV1} from '@albert-io/models';
import type {IndependentPracticeSubject} from './IndependentPractice/types';

type State = {
  areClassroomsLoading: boolean;
  areIndependentPracticeSubjectsErrored: boolean;
  areIndependentPracticeSubjectsLoading: boolean;
  areInvitationsLoading: boolean;
  classroomCode: string;
  classrooms: List<ClassroomModelV1>;
  duplicateClassroom: ClassroomModelV1 | null;
  exceededHardCapLimit: boolean;
  independentPracticeSubjects: IndependentPracticeSubject[];
  invitations: List<ClassroomInvitationModelV1>;
  isSidebarExpanded: boolean;
  isStudentEnrollmentError: boolean;
  isStudentEnrollmentPending: boolean;
  showJoinClassModal: boolean;
  studentEmail: string | null;
  studentHasInvalidEmail: boolean;
  userDefaultRoute: string | null;
};

type Actions = {
  setAreClassroomsLoading: (areClassroomsLoading: State['areClassroomsLoading']) => void;
  setAreIndependentPracticeSubjectsErrored: (
    areIndependentPracticeSubjectsErrored: State['areIndependentPracticeSubjectsErrored']
  ) => void;
  setAreIndependentPracticeSubjectsLoading: (
    areIndependentPracticeSubjectsLoading: State['areIndependentPracticeSubjectsLoading']
  ) => void;
  setAreInvitationsLoading: (areInvitationsLoading: State['areInvitationsLoading']) => void;
  setClassroomCode: (classroomCode: State['classroomCode']) => void;
  setClassrooms: (classrooms: State['classrooms']) => void;
  setDuplicateClassroom: (duplicateClassroom: State['duplicateClassroom']) => void;
  setExceededHardCapLimit: (hardCapExceeded: State['exceededHardCapLimit']) => void;
  setIndependentPracticeSubjects: (
    independentPracticeSubjects: State['independentPracticeSubjects']
  ) => void;
  setInvitations: (invitations: State['invitations']) => void;
  setIsSidebarExpanded: (isSidebarExpanded: State['isSidebarExpanded']) => void;
  setIsStudentEnrollmentError: (
    isStudentEnrollmentError: State['isStudentEnrollmentError']
  ) => void;
  setIsStudentEnrollmentPending: (
    isStudentEnrollmentPending: State['isStudentEnrollmentPending']
  ) => void;
  setShowJoinClassModal: (showJoinClassModal: State['showJoinClassModal']) => void;
  setStudentEmail: (studentEmail: NonNullable<State['studentEmail']>) => void;
  setStudentHasInvalidEmail: (hasInvalidEmail: State['studentHasInvalidEmail']) => void;
  setUserDefaultRoute: (userDefaultRoute: State['userDefaultRoute']) => void;
};

const initialState: State = {
  areClassroomsLoading: false,
  areIndependentPracticeSubjectsErrored: false,
  areIndependentPracticeSubjectsLoading: false,
  areInvitationsLoading: false,
  classroomCode: '',
  classrooms: List([]),
  duplicateClassroom: null,
  exceededHardCapLimit: false,
  independentPracticeSubjects: [],
  invitations: List([]),
  isSidebarExpanded: false,
  isStudentEnrollmentError: false,
  isStudentEnrollmentPending: false,
  showJoinClassModal: false,
  studentEmail: null,
  studentHasInvalidEmail: false,
  userDefaultRoute: null
};

export const useStudentHomepageStore = create<State & Actions>()(
  devtools(
    immer((set) => ({
      ...initialState,
      setAreClassroomsLoading: (areClassroomsLoading) => {
        set((state) => {
          state.areClassroomsLoading = areClassroomsLoading;
        });
      },
      setAreIndependentPracticeSubjectsErrored(areIndependentPracticeSubjectsErrored) {
        set((state) => {
          state.areIndependentPracticeSubjectsErrored = areIndependentPracticeSubjectsErrored;
        });
      },
      setAreIndependentPracticeSubjectsLoading(areIndependentPracticeSubjectsLoading) {
        set((state) => {
          state.areIndependentPracticeSubjectsLoading = areIndependentPracticeSubjectsLoading;
        });
      },
      setAreInvitationsLoading: (areInvitationsLoading) => {
        set((state) => {
          state.areInvitationsLoading = areInvitationsLoading;
        });
      },
      setClassroomCode(classroomCode) {
        set((state) => {
          state.classroomCode = classroomCode;
        });
      },
      setClassrooms: (classrooms) => {
        set((state) => {
          state.classrooms = classrooms;
        });
      },
      setDuplicateClassroom: (duplicateClassroom) => {
        set((state) => {
          state.duplicateClassroom = duplicateClassroom;
        });
      },
      setExceededHardCapLimit: (hardCapExceeded) => {
        set((state) => {
          state.exceededHardCapLimit = hardCapExceeded;
        });
      },
      setIndependentPracticeSubjects(independentPracticeSubjects) {
        set((state) => {
          state.independentPracticeSubjects = independentPracticeSubjects;
        });
      },
      setInvitations: (invitations) => {
        set((state) => {
          state.invitations = invitations;
        });
      },
      setIsSidebarExpanded: (isSidebarExpanded) => {
        set((state) => {
          state.isSidebarExpanded = isSidebarExpanded;
        });
      },
      setIsStudentEnrollmentError(isStudentEnrollmentError) {
        set((state) => {
          state.isStudentEnrollmentError = isStudentEnrollmentError;
        });
      },
      setIsStudentEnrollmentPending(isStudentEnrollmentPending) {
        set((state) => {
          state.isStudentEnrollmentPending = isStudentEnrollmentPending;
        });
      },
      setShowJoinClassModal: (showJoinClassModal) => {
        set((state) => {
          state.classroomCode = '';
          state.isStudentEnrollmentError = false;
          state.isStudentEnrollmentPending = false;
          state.showJoinClassModal = showJoinClassModal;
        });
      },
      setStudentEmail(studentEmail) {
        set((state) => {
          state.studentEmail = studentEmail;
        });
      },
      setStudentHasInvalidEmail: (hasInvalidEmail) => {
        set((state) => {
          state.studentHasInvalidEmail = hasInvalidEmail;
        });
      },
      setUserDefaultRoute(userDefaultRoute) {
        set((state) => {
          state.userDefaultRoute = userDefaultRoute;
        });
      }
    }))
  )
);
